type Pixel = { r: number; g: number; b: number; a: number }

const ALPHA_EMPTY = 12
const COLOR_DIST2 = 42 * 42
const BG = { r: 15, g: 23, b: 42 }

function read(data: Uint8ClampedArray, i: number): Pixel {
  return { r: data[i] ?? 0, g: data[i + 1] ?? 0, b: data[i + 2] ?? 0, a: data[i + 3] ?? 0 }
}

function dist2(a: Pixel, b: Pixel): number {
  const dr = a.r - b.r
  const dg = a.g - b.g
  const db = a.b - b.b
  const da = a.a - b.a
  return dr * dr + dg * dg + db * db + da * da
}

function isEmpty(p: Pixel, bg: Pixel): boolean {
  if (p.a < ALPHA_EMPTY) return true
  if (bg.a < ALPHA_EMPTY) return p.a < 40
  return dist2(p, bg) < COLOR_DIST2
}

function isLightPadding(p: Pixel): boolean {
  if (p.a < ALPHA_EMPTY) return true
  const max = Math.max(p.r, p.g, p.b)
  const min = Math.min(p.r, p.g, p.b)
  const sat = max === 0 ? 0 : (max - min) / max
  const lum = (0.299 * p.r + 0.587 * p.g + 0.114 * p.b) / 255
  return sat <= 0.22 && lum >= 0.82
}

function isTrimEmpty(p: Pixel, bg: Pixel, variant: LogoTrimVariant): boolean {
  if (isEmpty(p, bg)) return true
  return variant === 'light' && isLightPadding(p)
}

function cornerBg(data: Uint8ClampedArray, w: number, h: number): Pixel {
  const corners = [
    read(data, 0),
    read(data, (w - 1) * 4),
    read(data, (h - 1) * w * 4),
    read(data, ((h - 1) * w + (w - 1)) * 4),
  ]
  let best = corners[0]!
  let bestScore = -1
  for (const c of corners) {
    const score = corners.reduce((n, o) => n + (dist2(c, o) < COLOR_DIST2 ? 1 : 0), 0)
    if (score > bestScore) {
      best = c
      bestScore = score
    }
  }
  return best
}

export type LogoTrimVariant = 'sidebar' | 'light' | 'dark'

function punchEmpty(data: Uint8ClampedArray, bg: Pixel, variant: LogoTrimVariant): void {
  for (let i = 0; i < data.length; i += 4) {
    if (!isTrimEmpty(read(data, i), bg, variant)) continue
    data[i] = 0
    data[i + 1] = 0
    data[i + 2] = 0
    data[i + 3] = 0
  }
}

function recolorForSidebar(data: Uint8ClampedArray): void {
  for (let i = 0; i < data.length; i += 4) {
    const a = data[i + 3] ?? 0
    if (a < ALPHA_EMPTY) continue
    const r = data[i] ?? 0
    const g = data[i + 1] ?? 0
    const b = data[i + 2] ?? 0
    const max = Math.max(r, g, b)
    const min = Math.min(r, g, b)
    const sat = max === 0 ? 0 : (max - min) / max
    if (sat > 0.22) continue
    const t = (0.299 * r + 0.587 * g + 0.114 * b) / 255
    data[i] = Math.round(255 * (1 - t) + BG.r * t)
    data[i + 1] = Math.round(255 * (1 - t) + BG.g * t)
    data[i + 2] = Math.round(255 * (1 - t) + BG.b * t)
  }
}

export function trimImageWhitespace(src: string, variant: LogoTrimVariant = 'sidebar'): Promise<string> {
  return new Promise((resolve) => {
    const img = new Image()
    img.onload = () => {
      try {
        const w = img.naturalWidth
        const h = img.naturalHeight
        if (!w || !h) {
          resolve(src)
          return
        }
        const canvas = document.createElement('canvas')
        canvas.width = w
        canvas.height = h
        const ctx = canvas.getContext('2d')
        if (!ctx) {
          resolve(src)
          return
        }
        ctx.drawImage(img, 0, 0)
        const source = ctx.getImageData(0, 0, w, h)
        const bg = cornerBg(source.data, w, h)
        let minX = w
        let minY = h
        let maxX = -1
        let maxY = -1
        for (let y = 0; y < h; y++) {
          for (let x = 0; x < w; x++) {
            if (isTrimEmpty(read(source.data, (y * w + x) * 4), bg, variant)) continue
            if (x < minX) minX = x
            if (y < minY) minY = y
            if (x > maxX) maxX = x
            if (y > maxY) maxY = y
          }
        }
        if (maxX < minX) {
          minX = 0
          minY = 0
          maxX = w - 1
          maxY = h - 1
        }
        const cw = maxX - minX + 1
        const ch = maxY - minY + 1
        const out = document.createElement('canvas')
        out.width = cw
        out.height = ch
        const octx = out.getContext('2d')
        if (!octx) {
          resolve(src)
          return
        }
        octx.drawImage(img, minX, minY, cw, ch, 0, 0, cw, ch)
        const cropped = octx.getImageData(0, 0, cw, ch)
        if (variant === 'sidebar') recolorForSidebar(cropped.data)
        else punchEmpty(cropped.data, bg, variant)
        octx.putImageData(cropped, 0, 0)
        resolve(out.toDataURL('image/png'))
      } catch {
        resolve(src)
      }
    }
    img.onerror = () => resolve(src)
    img.src = src
  })
}
